import React from 'react';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CalendarNavIcon, BookOpenNavIcon, ShoppingCartNavIcon, MoreHorizontalNavIcon } from './NavIcons';

/**
 * BottomNav: shared bottom navigation for main app screens.
 *
 * Props:
 * - activeTab?: 'meal-plan' | 'shopping-list' | 'recipes' | 'more'
 * - onTabChange?: (tabId: string) => void
 *
 * Behavior:
 * - If onTabChange is provided, clicks call onTabChange(tabId).
 * - Otherwise it uses routing with useNavigate to switch screens.
 * - Active state uses activeTab if provided; otherwise derives from pathname.
 */
export default function BottomNav({ activeTab, onTabChange }) {
  const location = useLocation();
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const navigate = useNavigate();

  const pathToTab = (pathname) => {
    if (pathname === '/' || pathname.toLowerCase() === '/mealplan' || pathname.toLowerCase() === '/home') return 'meal-plan';
    if (pathname.toLowerCase().startsWith('/shopping')) return 'shopping-list';
    if (pathname.toLowerCase().startsWith('/allrecipes') || pathname.toLowerCase().startsWith('/recipes')) return 'recipes';
    if (pathname.toLowerCase().startsWith('/more')) return 'more';
    return 'meal-plan';
  };

  const resolvedActive = activeTab || pathToTab(location.pathname);

  const handleClick = (tab) => {
    if (typeof onTabChange === 'function') {
      onTabChange(tab);
      return;
    }
    // Route-based fallback
    switch (tab) {
      case 'meal-plan':
        navigate('/');
        break;
      case 'shopping-list':
        navigate('/ShoppingList');
        break;
      case 'recipes':
        navigate('/AllRecipes');
        break;
      case 'more':
        navigate('/More');
        break;
      default:
        navigate('/');
    }
  };

  // Responsive screen detection
useEffect(() => {
  const mediaQuery = window.matchMedia('(min-width: 768px)');
  
  const handleMediaChange = (e) => {
    setIsLargeScreen(e.matches);
  };
  
  // Set initial value
  setIsLargeScreen(mediaQuery.matches);
  
  // Listen for changes
  mediaQuery.addEventListener('change', handleMediaChange);
  
  // Cleanup
  return () => mediaQuery.removeEventListener('change', handleMediaChange);
}, []);

  return (
    <nav 
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 mx-auto"
          style={{ 
            maxWidth: isLargeScreen ? '1200px' : '448px',
            transition: 'max-width 0.3s ease'
          }}
        >
      <div className="flex items-center justify-around py-2">
        <button
          className={`flex flex-col items-center gap-1 py-2 px-4 relative ${resolvedActive === 'meal-plan' ? 'text-[var(--link-ingredient)]' : 'text-muted-foreground hover:text-foreground/80'}`}
          onClick={() => handleClick('meal-plan')}
        >
          <CalendarNavIcon isActive={resolvedActive === 'meal-plan'} />
          <span className="text-[10px]">Meal Plan</span>
          {resolvedActive === 'meal-plan' && (
            <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-0.5" style={{ width: '80%', backgroundColor: 'currentColor' }} />
          )}
        </button>

        <button
          className={`flex flex-col items-center gap-1 py-2 px-4 relative ${resolvedActive === 'shopping-list' ? 'text-[var(--link-ingredient)]' : 'text-muted-foreground hover:text-foreground/80'}`}
          onClick={() => handleClick('shopping-list')}
        >
          <ShoppingCartNavIcon isActive={resolvedActive === 'shopping-list'} />
          <span className="text-[10px]">Shopping List</span>
          {resolvedActive === 'shopping-list' && (
            <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-0.5" style={{ width: '80%', backgroundColor: 'currentColor' }} />
          )}
        </button>

        <button
          className={`flex flex-col items-center gap-1 py-2 px-4 relative ${resolvedActive === 'recipes' ? 'text-[var(--link-ingredient)]' : 'text-muted-foreground hover:text-foreground/80'}`}
          onClick={() => handleClick('recipes')}
        >
          <BookOpenNavIcon isActive={resolvedActive === 'recipes'} />
          <span className="text-[10px]">Recipes</span>
          {resolvedActive === 'recipes' && (
            <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-0.5" style={{ width: '80%', backgroundColor: 'currentColor' }} />
          )}
        </button>

        <button
          className={`flex flex-col items-center gap-1 py-2 px-4 relative ${resolvedActive === 'more' ? 'text-[var(--link-ingredient)]' : 'text-muted-foreground hover:text-foreground/80'}`}
          onClick={() => handleClick('more')}
        >
          <MoreHorizontalNavIcon isActive={resolvedActive === 'more'} />
          <span className="text-[10px]">More</span>
          {resolvedActive === 'more' && (
            <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-0.5" style={{ width: '80%', backgroundColor: 'currentColor' }} />
          )}
        </button>
      </div>
    </nav>
  );
}
